import { useCallback, useEffect, useRef, useState } from 'react'

interface UseDecisionKeyboardParams {
  /** Pending decision ids in display order — the cursor only moves over these. */
  pendingIds: string[]
  onApprove: (id: string) => void
  onReject: (id: string) => void
}

interface UseDecisionKeyboardResult {
  focusedId: string | null
  setFocusedId: (id: string) => void
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

/**
 * Keyboard navigation for the decision queue:
 *   a — approve focused   r — reject focused   n / ↓ — next   ↑ — previous
 *
 * The cursor lives over the pending decisions only and is kept valid as the
 * list changes (e.g. after an approval the focus advances to the next pending
 * decision). The focused card is scrolled into view.
 */
export function useDecisionKeyboard({
  pendingIds,
  onApprove,
  onReject,
}: UseDecisionKeyboardParams): UseDecisionKeyboardResult {
  const [focusedId, setFocusedId] = useState<string | null>(null)

  // Keep the latest values in refs so the keydown handler stays stable.
  const idsRef = useRef(pendingIds)
  const focusedRef = useRef(focusedId)
  const approveRef = useRef(onApprove)
  const rejectRef = useRef(onReject)
  idsRef.current = pendingIds
  focusedRef.current = focusedId
  approveRef.current = onApprove
  rejectRef.current = onReject

  // Keep focus on a still-pending decision; default to the first one.
  useEffect(() => {
    if (pendingIds.length === 0) {
      if (focusedId !== null) setFocusedId(null)
      return
    }
    if (focusedId === null || !pendingIds.includes(focusedId)) {
      setFocusedId(pendingIds[0])
    }
  }, [pendingIds, focusedId])

  // Scroll the focused card into view.
  useEffect(() => {
    if (!focusedId) return
    document.getElementById(`decision-${focusedId}`)?.scrollIntoView({ block: 'nearest' })
  }, [focusedId])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (isEditableTarget(e.target)) return

      const ids = idsRef.current
      if (ids.length === 0) return
      const current = focusedRef.current
      const idx = current ? ids.indexOf(current) : -1

      switch (e.key) {
        case 'n':
        case 'N':
        case 'ArrowDown': {
          e.preventDefault()
          const next = ids[(idx + 1 + ids.length) % ids.length]
          setFocusedId(next)
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          const prev = ids[(idx - 1 + ids.length) % ids.length]
          setFocusedId(prev)
          break
        }
        case 'a':
        case 'A': {
          if (!current) return
          e.preventDefault()
          approveRef.current(current)
          break
        }
        case 'r':
        case 'R': {
          if (!current) return
          e.preventDefault()
          rejectRef.current(current)
          break
        }
        default:
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const setFocused = useCallback((id: string) => setFocusedId(id), [])

  return { focusedId, setFocusedId: setFocused }
}
